import { defineComponent, ref, inject, computed, watch, onMounted } from "vue";
import { rxInitSubjectCallback, useObservable } from "src/plugins/rxjs/base";
import { debounceTime, tap } from "rxjs/operators";
export default defineComponent({
    name: "pic-set",
    props: {
        allowOtherFormats: Boolean,
        centered: Boolean,
        showLoad: Boolean,
        draggable: {
            type: String,
            default: "false",
        },
        originalSrc: String,
        originalSrcSet: String,
        webpSrcSet: String,
        sizes: String,
        picClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        loadClass: {
            type: Object,
            default: () => {
                return {};
            },
        },
        picStyle: Object,
        ratio: {
            type: Number,
            default: 2 / 3,
        },
        customWidth: Number,
        customHeight: Number,
        defaultLoaded: Boolean,
        imageStyle: Object,
        alternative: String,
        alternativeSrcSet: String,
        alternativeWebpSrcSet: String,
        alt: String,
    },
    emits: ["click", "image-load", "load", "error"],
    setup(props, context) {
        const error = ref(false);
        /*****
         * Urls
         *****/
        const parsedWebpSrcSet = computed(() => {
            if (!error.value) {
                return props.webpSrcSet;
            }
            return props.alternativeWebpSrcSet;
        });
        const parsedOriginalSrcSet = computed(() => {
            if (!error.value) {
                return props.originalSrcSet;
            }
            return props.alternativeSrcSet;
        });
        const parsedOriginalSrc = computed(() => {
            if (props.originalSrc && !error.value) {
                return props.originalSrc;
            }
            return props.alternative;
        });
        /*****
         * Image style
         *****/
        const whiteBg = inject("whiteBg", false);
        // if chrome, use image-rendering: -webkit-optimize-contrast
        // because chrome auto makes some images blur
        const isChrome = ref(false);
        onMounted(() => {
            if (typeof window !== "undefined") {
                isChrome.value = !!window?.chrome;
            }
        });
        const parsedPicStyle = computed(() => {
            return {
                ...props.picStyle,
                'height': 'auto'
            };
        });
        const widthHeightCalc = ref({
            width: 0,
            height: 0,
        });
        /*****
         * Actual size of image
         *****/
        function setSize() {
            const sizeDict = {
                width: "",
                height: "",
            };
            if ((!props.customWidth || !props.customHeight) &&
                props.ratio) {
                function toFraction(x) {
                    if (x === 0)
                        return [0, 1];
                    if (x < 0)
                        x = -x;
                    const tolerance = 0.0001;
                    let num = 1, den = 1;
                    function iterate() {
                        const R = num / den;
                        if (Math.abs((R - x) / x) < tolerance)
                            return;
                        if (R < x)
                            num++;
                        else
                            den++;
                        iterate();
                    }
                    iterate();
                    return [num, den];
                }
                const fraction = toFraction(props.ratio);
                sizeDict.width = fraction[1];
                sizeDict.height = fraction[0];
            }
            // override with custom width and height
            // in props
            widthHeightCalc.value = {
                ...sizeDict,
                ...(props.customWidth
                    ? {
                        width: props.customWidth,
                    } : {}),
                ...(props.customHeight
                    ? {
                        height: props.customHeight,
                    } : {})
            };
        }
        setSize();
        watch(parsedOriginalSrc, (newValue) => {
            if (!newValue)
                return;
            setSize();
        });
        /******
         * Load image
         *****/
        const imagesLoaded = ref(props.defaultLoaded);
        const { subject: delayLoad$ } = rxInitSubjectCallback();
        useObservable(delayLoad$.pipe(debounceTime(200), tap(() => {
            imagesLoaded.value = true;
            context.emit("image-load");
        })));
        const { subject: debouncedImgLoad$ } = rxInitSubjectCallback();
        const imageRef = ref(null);
        /**
         * When image is loaded
         */
        function imageLoad() {
            debouncedImgLoad$.next("");
        }
        useObservable(debouncedImgLoad$.pipe(debounceTime(100), tap(() => {
            if (imageRef.value) {
                // get actual size of the images
                context.emit("load", {
                    width: imageRef.value.naturalWidth,
                    height: imageRef.value.naturalHeight,
                });
            }
            delayLoad$.next("");
        })));
        /**
         * When image emits error
         */
        function checkError() {
            context.emit("error");
            error.value = true;
        }
        /****
         * Whether to allow/disallow drag
         ****/
        const injectedDraggable = inject("draggable", "false");
        const isDraggable = computed(() => {
            if (props.draggable === "true" || injectedDraggable === "true") {
                return "true";
            }
            return "false";
        });
        return {
            parsedWebpSrcSet,
            parsedOriginalSrcSet,
            parsedOriginalSrc,
            isChrome,
            parsedPicStyle,
            whiteBg,
            widthHeightCalc,
            error,
            imageRef,
            imagesLoaded,
            checkError,
            imageLoad,
            isDraggable,
        };
    },
});
